import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from 'react';
import parse from 'style-to-object';
import './draggable-entity-card.css';
import {
	ENTITY_FA_ICON_NAMES,
	getCanvasEntityDOMId,
	resolveEntityColorClass,
	getCardGradientStatement,
	resolveCardSubheader,
	fromDisabledReasonsToCardState
} from '../../services/editor/ui';
import Button from '../../lib/button/Button';
import { Canvas } from '../../lib/canvas';
import InputMapProperty from './InputMapProperty';
import TestEntityButton from './TestEntityButton';
import SuccessFailureNodes from './SuccessFailureNodes';
import {
	OperationState,
	EntityType,
	ConditionState,
	ValueDescriptorState,
	ProjectState,
	createStateFromTypeAndSubscribe,
	IValueDescriptorTransfer,
	ChangeSet,
	ChangeSetEntityChangeType,
	ActionInputMapState,
	GroupOperatorTypes,
	BUILT_IN_BASE_ENTITY_IDS,
	ActionDescriptorState,
	IActionDescriptorTransfer,
	CanvasEntityConnectionDisabledReason,
	CanvasEntityDisabledReason,
	CanvasEntityOtherDisabledReason,
	CanvasEntityTestingDisabledReason,
	ALL_CONNECTION_DISABLED_REASONS,
	addDisabledReasons,
	removeDisabledReasons,
	READABLE_ENTITY_TYPES,
	lowercaseFirstLetter,
	StandaloneOperatorTypes,
	StateMutationAction,
  entityDisabledReasonToShortReadableError,
  safeResolveEntityName
} from '@elyx-code/project-logic-tree';
import CanvasPopup, { useCanvasPopup } from '../../services/editor/CanvasPopup';
import CanvasPopupBaseComponent from '../../lib/popup/CanvasPopupBaseComponent';
import CanvasDropdownMenuCard from '../../lib/dropdown/CanvasDropdownMenuCard';
import LogicErrorIconMenu from './logic-error/LogicErrorIconMenu';
import { useNodeDescriptionPopup } from './useNodeDescriptionPopup';
import { ICanvasElementComponentProps } from '../../services/editor/CanvasElement';
import NewDynamicInputButton from './new-card-input-button/NewDynamicInputButton';
import { ConnectionNodeType, EditorService } from '../../services/editor';
import NestedCondition from './NestedCondition';
import UnreachableMarker from './UnreachableMarker';
import { popupNotification } from '../../popup-notification';

interface IConditionCardProps extends ICanvasElementComponentProps {
	entity: ConditionState;
}

const ConditionCard = (props: IConditionCardProps) => {
	const rootElementRef = useRef(null);

	useEffect(() => {
		const element = rootElementRef.current;

		if (!!element && document.contains(element)) {
			props.onMount?.(element);
		}

		// When the component is unmounted
		return () => {
			props.onUnmount?.();
		};
	}, []);

	useEffect(() => {
		const element = rootElementRef.current;

		props.onUpdate?.(element);
	});

	useLayoutEffect(() => {
		props.onBeforeUpdate?.();
	}, []);

	const [disabledReasons, setDisabledReasons] = useState<
		CanvasEntityDisabledReason[]
	>([]);

	const [updateKey, setValue] = useState(0);

	const useForceUpdate = () => {
		return () => setValue((value) => value + 1);
	};
	const forceUpdate = useForceUpdate(); // Update the component

	useNodeDescriptionPopup({
		entity: props.entity,
		canvasElement: props.canvasElement,
		project: props.project
	});

	const memoizedCanvasPopupBody = useCallback(
		(popup: CanvasPopup) => (
			<CanvasPopupBaseComponent
				id={
					'canvas-small-entity-card-menu-button-' +
					props.entity.id +
					'--popup--body'
				}
			>
				<CanvasDropdownMenuCard
					options={[
						{
							project: props.project,
							onClick: async () => {
								await props.project.remove(props.entity.id);
							},
							label: 'Delete',
							color: 'error',
							iconEnd: 'fa-solid fa-trash-can',
							disabled: !entity.deletable,
							tooltip: entity.deletable
								? 'Forever delete this condition'
								: 'This condition cannot be deleted. Because the system need to refer to it over time.',
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.entity.type +
								'-card--' +
								props.entity.id +
								'--delete'
						},
						{
							project: props.project,
							onClick: async () => {
								props.project?.canvas?.unselectAll();

								EditorService.navigate(
									`${props.project?.editorBasePath}/entity/${props.entity.type}/${props.entity.id}`
								);
							},
							label: 'Edit',
							iconEnd: 'fa-solid fa-pencil',
							disabled: false,
							tooltip: !entity.editable
								? 'This condition has been locked and can no longer be edited'
								: 'Edit this ' +
									lowercaseFirstLetter(
										READABLE_ENTITY_TYPES[props.entity.type]
											.shortSingular
									),
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.entity.type +
								'-card--' +
								props.entity.id +
								'--edit'
						},
						{
							project: props.project,
							onClick: async () => {
								await props.project.clearEntityTestValues(props.entity);
							},
							label: 'Clear test values',
							iconEnd: 'fa-solid fa-eraser',
							disabled: !props.project.hasTestValuesForEntity(props.entity),
							tooltip: props.project.hasTestValuesForEntity(props.entity)
								? 'Clear all test values (purple overrides) for this node'
								: 'No test values to clear for this node',
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.entity.type +
								'-card--' +
								props.entity.id +
								'--clear-test-values'
						},
						{
							project: props.project,
							onClick: async () => {
								const changeSet =
									props.project.logic.addChangeSet(
										new ChangeSet(
											props.project.logic,
											ProjectState.sessionAuthor,
											new Date().toISOString(),
											entity,
											false,
											StateMutationAction.RecalculateCondition
										)
									);

								entity.afterAllChildrenInitialized(changeSet);

								props.project.renderAndCloseChangeSet(
									changeSet
								);
							},
							label: 'Recalculate',
							color: 'warning',
							// Bug icon
							iconEnd: 'fa-solid fa-bug',
							disabled: false,
							// Lower case the first letter of the resolved readable name
							tooltip:
								'Recalculate the state of this ' +
								lowercaseFirstLetter(
									READABLE_ENTITY_TYPES[props.entity.type]
										.shortSingular
								),
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.entity.type +
								'-card--' +
								props.entity.id +
								'--recalculate'
						},
						{
							project: props.project,
							onClick: async () => {
								navigator.clipboard.writeText(props.entity.id);
								popupNotification({
									text: `Copied ID: ${props.entity.id}`,
									color: 'success'
								});
							},
							label: 'Copy ID',
							color: 'warning',
							iconEnd: 'fa-solid fa-bug',
							tooltip:
								'Copy this node ID to clipboard for debugging',
							parentCanvasObject: popup.self,
							id: 'copy-id-' + props.entity.id
						},
						{
							project: props.project,
							onClick: async () => {
								await props.project.recalculateValidateEntity(
									props.entity
								);
								popupNotification({
									text: `Re-validated node successfully`,
									color: 'success'
								});
							},
							label: 'Re-validate',
							color: 'warning',
							iconEnd: 'fa-solid fa-bug',
							tooltip:
								"Re-validate this node's state and its errors",
							parentCanvasObject: popup.self,
							id: 're-validate-' + props.entity.id
						}
					]}
				/>
			</CanvasPopupBaseComponent>
		),
		[props.entity, props.project]
	);

	const canvasPopup = useCanvasPopup(
		{
			editor: props.project,
			id:
				'canvas-small-entity-card-menu-button-' +
				props.entity.id +
				'--popup',
			anchorQuerySelector:
				'#canvas-small-entity-card-menu-button-' + props.entity.id,
			arrowClasses: ['dropdown-menu-card-arrow'],
			parentCanvasObject: props.canvasElement.draggableObject,
			arrowContainerSelector:
				'#canvas-small-entity-card-menu-button-' +
				props.entity.id +
				'--popup--body'
		},
		memoizedCanvasPopupBody
	);

	useEffect(() => {
		// --- Initial Setup Logic ---
		const initialNewDisabledReasons: CanvasEntityOtherDisabledReason[] = [];

		// @ts-ignore
		if (props.entity.suggestion) {
			initialNewDisabledReasons.push(
				CanvasEntityOtherDisabledReason.Suggestion
			);
		}

		if (props.project.canvas?.disabled) {
			initialNewDisabledReasons.push(
				CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
			);
		}

		if (!!initialNewDisabledReasons.length) {
			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(
					currentReasons,
					initialNewDisabledReasons
				);
				return config.reasons;
			});
		} else {
			// This handles the case where canvas was disabled but is now enabled
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		}

		const handleCanvasGloballyEnabled = () => {
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		};
		const handleCanvasGloballyDisabled = () => {
			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		};

		const handleEntityUpdate = (payload: any) => {
			let hasUpdated = false;
			const newDisabledReasons: CanvasEntityTestingDisabledReason[] = [];
			const disabledReasonsToRemove: CanvasEntityTestingDisabledReason[] =
				[];

			props.project.test?.disabledConnectionsNotInTest(
				props.canvasElement.draggableObject,
				props.entity
			);

			if (
				!!props.project.test &&
				!props.project.test?.hasEntity(props.entity) &&
				!props.project.test?.getCanBeAddedToTest(props.entity)
			) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				);
			}

			if (
				(props.project.test?.hasEntity(props.entity) ||
					props.project.test?.getCanBeAddedToTest(props.entity)) &&
				props.canvasElement.draggableObject.disabled
			) {
				props.project.canvas?.enable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
				disabledReasonsToRemove.push(
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				);
			}

			if (
				!!props.project.test &&
				!props.project.test?.hasEntity(props.entity)
			) {
				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
				);
			} else if (
				!!props.project.test &&
				props.project.test?.hasEntity(props.entity)
			) {
				disabledReasonsToRemove.push(
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
				);
			}

			if (newDisabledReasons.length || disabledReasonsToRemove.length) {
				setDisabledReasons((currentReasons) => {
					const config = addDisabledReasons(
						currentReasons,
						newDisabledReasons
					);
					const finalConfig = removeDisabledReasons(
						config.reasons,
						disabledReasonsToRemove
					);
					return finalConfig.reasons;
				});
				hasUpdated = true;
			}

			if (!hasUpdated) {
				forceUpdate();
			}
		};

		const handleTestSetupStarted = () => {
			const newDisabledReasons: CanvasEntityTestingDisabledReason[] = [];

			if (
				!!props.project.test &&
				!props.project.test?.hasEntity(props.entity) &&
				!props.project.test?.getCanBeAddedToTest(props.entity)
			) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
				props.project.test?.disabledConnectionsNotInTest(
					props.canvasElement.draggableObject,
					props.entity
				);
				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				);
			}

			if (!props.project.test?.hasEntity(props.entity)) {
				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
				);
			}

			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(
					currentReasons,
					newDisabledReasons
				);
				return config.reasons;
			});
		};

		const handleExecutionStart = () => {
			if (!!props.project.test?.hasEntity(props.entity)) {
				forceUpdate();
			}
		};

		const handleTestExited = () => {
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest,
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				]);
				return config.reasons;
			});

			if (props.canvasElement.draggableObject.disabled) {
				props.project.canvas?.enable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
			}

			props.canvasElement.draggableObject.connections.forEach(
				(connection) => {
					connection.enable();
					connection.element.classList.remove('disabled');

					if (connection.complementaryLineElement) {
						connection.complementaryLineElement.classList.remove(
							'disabled'
						);
					}
				}
			);
		};

		const handleCanvasConnectionStart = (payload: any) => {
			if (!payload.compatible?.[props.entity.id]) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);

				const newOwnConnectionDisabledReasons: CanvasEntityConnectionDisabledReason[] =
					payload.incompatible?.[props.entity.id]?.reasons || [];

				setDisabledReasons((currentReasons) => {
					const config = addDisabledReasons(
						currentReasons,
						newOwnConnectionDisabledReasons
					);
					return config.reasons;
				});
			} else {
				forceUpdate();
			}
		};

		const handleCanvasConnectionEnd = () => {
			props.project.canvas?.enable(props.canvasElement.draggableObject, {
				quiet: true
			});

			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(
					currentReasons,
					ALL_CONNECTION_DISABLED_REASONS
				);
				return config.reasons;
			});
		};

		props.project.on(
			'canvas-globally-enabled',
			handleCanvasGloballyEnabled
		);
		props.project.on(
			'canvas-globally-disabled',
			handleCanvasGloballyDisabled
		);
		props.project.on(props.entity.id, handleEntityUpdate);
		props.project.on('test-setup-started', handleTestSetupStarted);
		props.project.on('execution-start', handleExecutionStart);
		props.project.on('test-exited', handleTestExited);
		props.project.on(
			'canvas-connection-start',
			handleCanvasConnectionStart
		);
		props.project.on('canvas-connection-end', handleCanvasConnectionEnd);

		return () => {
			props.project.off(
				'canvas-globally-enabled',
				handleCanvasGloballyEnabled
			);
			props.project.off(
				'canvas-globally-disabled',
				handleCanvasGloballyDisabled
			);
			props.project.off(props.entity.id, handleEntityUpdate);
			props.project.off('test-setup-started', handleTestSetupStarted);
			props.project.off('execution-start', handleExecutionStart);
			props.project.off('test-exited', handleTestExited);
			props.project.off(
				'canvas-connection-start',
				handleCanvasConnectionStart
			);
			props.project.off(
				'canvas-connection-end',
				handleCanvasConnectionEnd
			);
		};
	}, [
		props.project,
		props.entity,
		props.canvasElement,
		updateKey,
		// @ts-ignore
		props.entity.suggestion
	]);

	useEffect(() => {
		props.canvasElement.draggableObject.updateConnections();
	});

	const entity = props.entity;

	const classes = ['canvas-draggable-card'];

	const dynamicInputsValueDescriptors: ValueDescriptorState[] = [];

	const commonInputButtonAllowed =
		!props.project.test &&
		!disabledReasons.includes(CanvasEntityOtherDisabledReason.Suggestion) &&
		!disabledReasons.includes(
			CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
		) &&
		!disabledReasons.includes(
			CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
		) &&
		!disabledReasons.includes(
			CanvasEntityTestingDisabledReason.CannotBeAddedToTest
		) &&
		(!props.project.onGoingConnectionNodeType ||
			(props.project.onGoingConnectionNodeType &&
				props.project.onGoingConnectionNodeType ===
					ConnectionNodeType.PropertyOut)) &&
		!disabledReasons.includes(
			CanvasEntityConnectionDisabledReason.NotInScope
		);

	const showNewDynamicInputButton = commonInputButtonAllowed;

	const showNewNestedButton =
		commonInputButtonAllowed &&
		[GroupOperatorTypes.And, GroupOperatorTypes.Or].includes(
			entity.declaration.id as GroupOperatorTypes
		);

	return (
		<div
			key={entity.id}
			ref={rootElementRef}
			className={classes.join(' ')}
			id={getCanvasEntityDOMId(props.entity)}
			{...{
				[Canvas.OBJ_ATTR_NAME]: getCanvasEntityDOMId(props.entity)
			}}
			onClick={(e) => {
				e.stopPropagation();
			}}
		>
			<TestEntityButton
				entity={props.entity}
				canvasElement={props.canvasElement}
				project={props.project}
				parentDisabledReasons={disabledReasons}
			/>
			{/* Floats "Unreachable!" above the right side of the header when
				the entity carries the blocked-reachability error. Same DOM
				placement as VariableScopeMarker on VariableCard so the
				`position: absolute` offsets resolve against the outer
				canvas-draggable-card div. Returns null when reachable. */}
			<UnreachableMarker
				entity={props.entity}
				parentDisabledReasons={disabledReasons}
				project={props.project}
				draggableObject={props.canvasElement.draggableObject}
			/>
			<div
				className={[
					'canvas-draggable-card__body',
					...(!!disabledReasons.length
						? ['canvas-draggable-card__body__disabled']
						: []),
					resolveEntityColorClass(props.entity.type, {
						state: fromDisabledReasonsToCardState(disabledReasons)
					})
				].join(' ')}
			>
				<header
					className="canvas-draggable-card__header"
					{...{
						[Canvas.OBJ_ATTR_NAME]:
							getCanvasEntityDOMId(props.entity) + '--header'
					}}
					style={parse(
						getCardGradientStatement(entity, {
							state: fromDisabledReasonsToCardState(
								disabledReasons
							)
						})
					)}
					onMouseOver={(e: any) => {
						if (!!props.project.canvas?.activeConnectionDrawing) {
							if (
								!!props.project
									.onGoingConnectionIncompatibleEntities?.[
									props.entity.id
								]
							) {
								// Read the disabled reasons and add a tooltip to the mouse
								const reasonsMessage =
									props.project.onGoingConnectionIncompatibleEntities?.[
										props.entity.id
									].reasons
										.map((reason) =>
											entityDisabledReasonToShortReadableError(
												reason
											)
										)
										.join('.\n');

								props.project.canvas?.showTooltip(
									'connection-disabled-reasons-test-entity-button',
									reasonsMessage
								);
								if (props.project.canvas) {
									props.project.canvas.tooltip.style.fontSize =
										'28px';
									props.project.canvas.canvasElement.style.cursor =
										'not-allowed';
								}
							}
						}
					}}
					onMouseLeave={(e: any) => {
						props.project.canvas?.hideTooltip(
							'connection-disabled-reasons-test-entity-button'
						);
					}}
				>
					<div className="canvas-draggable-card__header__left-block">
						<i
							className={`canvas-draggable-card-header__icon fa-solid fa-${
								ENTITY_FA_ICON_NAMES[entity.type] ||
								'rectangle-list'
							}`}
						/>
						<span className="canvas-draggable-card__header__labels">
							<h3>
								{safeResolveEntityName(
									entity,
									props.project.logic
								)}
							</h3>
							<p>{resolveCardSubheader(entity)}</p>
						</span>
					</div>
					<div className="canvas-draggable-card__header__actions">
						<LogicErrorIconMenu
							entity={entity}
							project={props.project}
							canvasElement={props.canvasElement}
							parentDisabledReasons={disabledReasons}
						/>
						<Button
							variant="text"
							color="grey"
							iconEnd="fa-solid fa-ellipsis-v"
							size="small"
							ref={canvasPopup.anchorRef}
							className="canvas-small-entity-card-menu-button"
							id={`canvas-small-entity-card-menu-button-${entity.id}`}
							disabled={!!disabledReasons.length}
							onClick={(e: any) => {
								e.stopPropagation();
								canvasPopup?.toggle();
							}}
							onDoubleClick={(
								e: React.MouseEvent<HTMLButtonElement>
							) => {
								e.stopPropagation();
								if (disabledReasons.length) return;
								props.project?.canvas?.unselectAll();
								EditorService.navigate(
									`${props.project?.editorBasePath}/entity/${props.entity.type}/${props.entity.id}`
								);
							}}
						/>
						<SuccessFailureNodes
							entity={entity}
							draggableObject={
								props.canvasElement.draggableObject
							}
							project={props.project}
							parentDisabledReasons={disabledReasons}
						/>
					</div>
				</header>
				<div className="canvas-draggable-card__content">
					{entity.inputs.length > 0 && (
						<ul className="canvas-draggable-card__property-list">
							{entity.inputs
								.sort((a, b) => {
									// Sort by `.index` property
									if (a.index < b.index) {
										return -1;
									}
									if (a.index > b.index) {
										return 1;
									}
									return 0;
								})
								.map((inputMap) => {
									return (
										<InputMapProperty
											key={inputMap.id}
											inputMap={inputMap}
											entity={props.entity}
											canvasElement={props.canvasElement}
											project={props.project}
											parentDisabledReasons={
												disabledReasons
											}
										/>
									);
								})}
						</ul>
					)}
					{(entity.andChildrenGroup || []).length > 0 && (
						<ul className="canvas-draggable-card__property-list">
							{(entity.andChildrenGroup || [])
								.sort((a, b) => {
									// Sort by `.index` property
									if (a.index < b.index) {
										return -1;
									}
									if (a.index > b.index) {
										return 1;
									}
									return 0;
								})
								.map((nestedCondition, index) => {
									return (
										<>
											<NestedCondition
												key={nestedCondition.id}
												entity={nestedCondition}
												canvasElement={
													props.canvasElement
												}
												project={props.project}
												parentDisabledReasons={
													disabledReasons
												}
											/>
											{/* If it isn't the last item, add an 'AND' */}
											{index <
												(entity.andChildrenGroup || [])
													.length -
													1 && (
												<span
													key={
														nestedCondition.id +
														'-and-span'
													}
													className="canvas-draggable-card__property-list__and"
													style={{
														color: 'rgba(0,0,0, 0.6)',
														fontSize: '12px',
														width: '100%',
														padding: '0 14px'
													}}
												>
													AND
												</span>
											)}
										</>
									);
								})}
						</ul>
					)}
					{(entity.orChildrenGroup || []).length > 0 && (
						<ul className="canvas-draggable-card__property-list">
							{(entity.orChildrenGroup || [])
								.sort((a, b) => {
									// Sort by `.index` property
									if (a.index < b.index) {
										return -1;
									}
									if (a.index > b.index) {
										return 1;
									}
									return 0;
								})
								.map((nestedCondition, index) => {
									return (
										<>
											<NestedCondition
												key={nestedCondition.id}
												entity={nestedCondition}
												canvasElement={
													props.canvasElement
												}
												project={props.project}
												parentDisabledReasons={
													disabledReasons
												}
											/>
											{/* If it isn't the last item, add an 'AND' */}
											{index <
												(entity.orChildrenGroup || [])
													.length -
													1 && (
												<span
													key={
														nestedCondition.id +
														'-or-span'
													}
													className="canvas-draggable-card__property-list__and"
													style={{
														color: 'rgba(0,0,0, 0.6)',
														fontSize: '12px',
														width: '100%',
														padding: '0 14px'
													}}
												>
													OR
												</span>
											)}
										</>
									);
								})}
						</ul>
					)}
					{showNewNestedButton && (
						<div
							className={
								'canvas-draggable-card__action align-left'
							}
						>
							<Button
								iconStart="fa-solid fa-plus"
								size="small"
								disabled={!!disabledReasons.length}
								onClick={async () => {
									// Create a new nested condition
									const changeSet =
										props.project.logic.addChangeSet(
											new ChangeSet(
												props.project.logic,
												ProjectState.sessionAuthor,
												new Date().toISOString(),
												entity,
												false,
												StateMutationAction.AddNestedAndCondition
											)
										);

									const newConditionDescriptor =
										props.project.logic.getBuiltIn(
											BUILT_IN_BASE_ENTITY_IDS.condition[
												StandaloneOperatorTypes
													.BooleanIsTrue
											].id
										) as ActionDescriptorState;

									const newConditionTransfer =
										ConditionState.new(changeSet);
									newConditionTransfer.parent =
										entity.toReference();
									newConditionTransfer.declaration =
										newConditionDescriptor.toJSON() as IActionDescriptorTransfer;
									newConditionTransfer.index = (
										entity.andChildrenGroup || []
									).length;

									const newInstance =
										createStateFromTypeAndSubscribe(
											newConditionTransfer,
											props.project.logic
										) as ConditionState;

									newInstance.hydrateAncestors();
									newInstance.addSelfToProject(changeSet);

									changeSet.add(
										newInstance,
										ChangeSetEntityChangeType.Added
									);

									newInstance.initChildren(changeSet);

									EditorService.navigate(
										`${props.project?.editorBasePath}/entity/${EntityType.Condition}/${newInstance.id}`
									);

									await props.project.renderAndCloseChangeSet(
										changeSet
									);
								}}
								style={parse('background: #7e55e6;')}
							>
								Condition
							</Button>
						</div>
					)}
					{showNewDynamicInputButton &&
						dynamicInputsValueDescriptors
							.sort((a, b) => {
								// Sort by `.index` property
								if (a.index < b.index) {
									return -1;
								}
								if (a.index > b.index) {
									return 1;
								}
								return 0;
							})
							.map((valueDescriptor) => {
								return (
									<div
										key={valueDescriptor.id}
										className={[
											'canvas-draggable-card__action-alt',
											'align-left'
										].join(' ')}
									>
										<NewDynamicInputButton
											iconStart="fa-solid fa-plus"
											size="small"
											parentDisabledReasons={
												disabledReasons
											}
											entity={
												entity as
													| OperationState
													| ConditionState
											}
											inputValueDescriptor={
												valueDescriptor
											}
											project={props.project}
											canvasElement={props.canvasElement}
											onClick={() => {
												const changeSet =
													props.project.logic.addChangeSet(
														new ChangeSet(
															props.project.logic,
															ProjectState.sessionAuthor,
															new Date().toISOString(),
															entity,
															false,
															StateMutationAction.AddInputToCondition
														)
													);

												const newInputMapTransfer =
													ActionInputMapState.new(
														changeSet
													);
												newInputMapTransfer.parent =
													entity.toReference();
												newInputMapTransfer.declaration =
													valueDescriptor.toJSON() as IValueDescriptorTransfer;

												const newInputMap =
													createStateFromTypeAndSubscribe(
														newInputMapTransfer,
														props.project.logic
													) as ValueDescriptorState;

												newInputMap.hydrateAncestors();

												changeSet.add(
													newInputMap,
													ChangeSetEntityChangeType.Added
												);

												newInputMap.addSelfToProject(
													changeSet
												);
												newInputMap.afterAllChildrenInitialized(
													changeSet
												);

												entity.afterAllChildrenInitialized(
													changeSet
												);

												EditorService.navigate(
													`${props.project?.editorBasePath}/entity/${EntityType.InputMap}/${newInputMap.id}`
												);

												props.project.renderAndCloseChangeSet(
													changeSet
												);
											}}
										>
											{safeResolveEntityName(
												valueDescriptor,
												props.project.logic
											)}
										</NewDynamicInputButton>
									</div>
								);
							})}
				</div>
			</div>
		</div>
	);
};

export default ConditionCard;
